
import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/userService';
import { storageService } from '../services/storageService';
import type { UserProfile, ExternalEvent, PrivacyZone, FavoriteGroupRide } from '../types';
import { PencilIcon, PlusIcon, TrashIcon, CameraIcon } from '../components/icons/PlaybackIcons';
import { wahooIntegrationService } from '../services/wahooIntegrationService';
import { garminIntegrationService } from '../services/garminIntegrationService';
import { hammerheadIntegrationService } from '../services/hammerheadIntegrationService';
import EventSearchModal from '../components/EventSearchModal';
import PrivacyZoneManager from '../components/PrivacyZoneManager';
import SEO from '../components/SEO';

const UserProfilePage: React.FC = () => {
  const { currentUser, isAdmin, resetPassword } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isVerifyingWahoo, setIsVerifyingWahoo] = useState(false);
  const [isVerifyingHammerhead, setIsVerifyingHammerhead] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [resetPasswordStatus, setResetPasswordStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<Partial<UserProfile>>({
    firstName: '',
    lastName: '',
    heightFeet: 0,
    heightInches: 0,
    age: 0,
    weightLbs: 0,
    bikeWeightLbs: 0,
    sex: '',
    teamName: '',
    usacLicenseNumber: '',
    ftp: 0,
    estimatedMatches: 0,
    privacyZones: []
  });

  // Track planned events separately or inside formData
  const [plannedEvents, setPlannedEvents] = useState<ExternalEvent[]>([]);
  const [favoriteGroupRides, setFavoriteGroupRides] = useState<FavoriteGroupRide[]>([]);

  useEffect(() => {
    const loadProfile = async () => {
      if (currentUser) {
        try {
          const profile = await userService.getUserProfile(currentUser.uid);
          if (profile) {
            setFormData(profile);
            if (profile.plannedEvents) {
                setPlannedEvents(profile.plannedEvents);
            }
            if (profile.favoriteGroupRides) {
                setFavoriteGroupRides(profile.favoriteGroupRides);
            }
          } else {
            // Initialize with basic auth data if profile doesn't exist
            const names = currentUser.displayName?.split(' ') || [];
            setFormData(prev => ({
                ...prev,
                firstName: names[0] || '',
                lastName: names.slice(1).join(' ') || '',
                email: currentUser.email || '',
                privacyZones: []
            }));
          }
        } catch (error) {
          console.error("Failed to load profile", error);
          setMessage({ text: "Failed to load profile data.", type: 'error' });
        } finally {
          setIsLoading(false);
        }
      }
    };
    loadProfile();
  }, [currentUser]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) : value
    }));
  };

  const handlePrivacyZonesChange = (zones: PrivacyZone[]) => {
      setFormData(prev => ({ ...prev, privacyZones: zones }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    if (formData.bikeWeightLbs !== undefined && formData.bikeWeightLbs !== null && formData.bikeWeightLbs !== 0) {
      if (formData.bikeWeightLbs < 10 || formData.bikeWeightLbs > 40) {
        setMessage({ text: "Bike weight must be between 10 lbs and 40 lbs.", type: 'error' });
        return;
      }
    }

    setIsSaving(true);
    setMessage(null);

    try {
      await userService.updateUserProfile(currentUser.uid, {
        ...formData,
        email: currentUser.email || ''
      });
      setMessage({ text: "Profile updated successfully!", type: 'success' });
    } catch (error) {
      setMessage({ text: "Failed to save profile.", type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageClick = () => {
      fileInputRef.current?.click();
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0] && currentUser) {
          const file = e.target.files[0];
          
          // Validate size (e.g. 5MB)
          if (file.size > 5 * 1024 * 1024) {
              setMessage({ text: "Image size must be less than 5MB.", type: 'error' });
              return;
          }

          setIsUploadingImage(true);
          try {
              const photoURL = await storageService.uploadProfilePicture(currentUser.uid, file);
              
              // 1. Update Firebase Auth Profile (Compat method)
              await currentUser.updateProfile({ photoURL });
              
              // 2. Update Firestore User Profile
              await userService.updateUserProfile(currentUser.uid, { photoURL });
              
              // 3. Force page reload to reflect changes across the app context
              window.location.reload();
              
          } catch (error) {
              console.error(error);
              setMessage({ text: "Failed to upload image.", type: 'error' });
          } finally {
              setIsUploadingImage(false);
          }
      }
  };

  const handleAddEvent = async (event: ExternalEvent) => {
      if (!currentUser) return;
      try {
          await userService.addPlannedEvent(currentUser.uid, event);
          setPlannedEvents(prev => [...prev, event]);
      } catch (e) {
          console.error(e);
          alert("Failed to add event to your calendar.");
      }
  };

  const handleRemoveEvent = async (event: ExternalEvent) => {
      if (!currentUser) return;
      // Optimistic update
      const oldEvents = [...plannedEvents];
      setPlannedEvents(prev => prev.filter(e => e.id !== event.id));

      try {
          await userService.removePlannedEvent(currentUser.uid, event);
      } catch (e) {
          console.error(e);
          alert("Failed to remove event.");
          setPlannedEvents(oldEvents); // Revert
      }
  };

  const handleRemoveFavoriteGroupRide = async (rideId: string) => {
      if (!currentUser) return;
      const oldRides = [...favoriteGroupRides];
      setFavoriteGroupRides(prev => prev.filter(r => r.id !== rideId));
      try {
          await userService.removeFavoriteGroupRide(currentUser.uid, rideId);
      } catch (e) {
          console.error(e);
          alert('Failed to remove saved ride.');
          setFavoriteGroupRides(oldRides); // Revert
      }
  };

  const handleDisconnectWahoo = async () => {
      if (!currentUser) return;
      if (window.confirm("Are you sure you want to disconnect your Wahoo account?")) {
          try {
              await wahooIntegrationService.disconnect();
              setFormData(prev => {
                  const newData = { ...prev } as any;
                  delete newData.wahoo;
                  return newData;
              });
              setMessage({ text: "Wahoo account disconnected.", type: 'success' });
          } catch (error) {
              console.error(error);
              setMessage({ text: "Failed to disconnect Wahoo.", type: 'error' });
          }
      }
  };

  const handleVerifyWahoo = async () => {
      setIsVerifyingWahoo(true);
      setMessage(null);
      try {
          const result = await wahooIntegrationService.verifyConnection();
          if (result) {
              setMessage({ text: `Connection Verified! Linked to Wahoo user: ${result}`, type: 'success' });
          } else {
              setMessage({ text: "Connection check failed. Your Wahoo token may be expired. Please disconnect and reconnect.", type: 'error' });
          }
      } catch (error) {
          setMessage({ text: "Error verifying connection.", type: 'error' });
      } finally {
          setIsVerifyingWahoo(false);
      }
  };

  const handleConnectWahoo = () => {
      wahooIntegrationService.connect();
  };

  const handleDisconnectHammerhead = async () => {
      if (!currentUser) return;
      if (window.confirm("Are you sure you want to disconnect your Hammerhead account?")) {
          try {
              await hammerheadIntegrationService.disconnect();
              setFormData(prev => {
                  const newData = { ...prev } as any;
                  delete newData.hammerhead;
                  return newData;
              });
              setMessage({ text: "Hammerhead account disconnected.", type: 'success' });
          } catch (error) {
              console.error(error);
              setMessage({ text: "Failed to disconnect Hammerhead.", type: 'error' });
          }
      }
  };

  const handleVerifyHammerhead = async () => {
      setIsVerifyingHammerhead(true);
      setMessage(null);
      try {
          const result = await hammerheadIntegrationService.verifyConnection();
          if (result) {
              setMessage({ text: `Hammerhead Connection Verified! Linked to user: ${result}`, type: 'success' });
          } else {
              setMessage({ text: "Connection check failed. Please disconnect and reconnect.", type: 'error' });
          }
      } catch (error) {
          setMessage({ text: "Error verifying connection.", type: 'error' });
      } finally {
          setIsVerifyingHammerhead(false);
      }
  };

  const handleConnectHammerhead = () => {
      hammerheadIntegrationService.connect();
  };

  const handleConnectGarmin = async () => {
      try {
          await garminIntegrationService.connect();
      } catch (e) {
          console.error(e);
          setMessage({ text: "Failed to initiate Garmin connection.", type: 'error' });
      }
  };

  const handleResetPassword = async () => {
      if (!currentUser?.email || !resetPassword) return;
      setResetPasswordStatus('sending');
      try {
          await resetPassword(currentUser.email);
          setResetPasswordStatus('success');
          setTimeout(() => setResetPasswordStatus('idle'), 5000);
      } catch (error) {
          console.error(error);
          setResetPasswordStatus('error');
          setTimeout(() => setResetPasswordStatus('idle'), 5000);
      }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full bg-gray-900 text-white">
        <svg className="animate-spin h-10 w-10 text-cyan-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-900 text-white p-4 sm:p-6 lg:p-8 overflow-y-auto">
      <SEO title="My Profile | RaceLineIQ" />
      <div className="max-w-4xl mx-auto space-y-8">
        
        {/* PROFILE CARD */}
        <div className="bg-gray-800 rounded-lg shadow-xl border border-gray-700 overflow-hidden">
            <div className="p-6 border-b border-gray-700 flex items-center gap-6">
                <div className="relative group cursor-pointer" onClick={handleImageClick}>
                    <img 
                        src={currentUser?.photoURL || `https://ui-avatars.com/api/?name=${currentUser?.displayName || 'User'}`} 
                        alt="Profile" 
                        className={`w-20 h-20 rounded-full border-4 border-gray-700 object-cover group-hover:opacity-75 transition-opacity ${isUploadingImage ? 'opacity-50' : ''}`}
                    />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <CameraIcon className="w-8 h-8 text-white drop-shadow-lg" />
                    </div>
                    {isUploadingImage && (
                        <div className="absolute inset-0 flex items-center justify-center">
                             <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                    )}
                    <input 
                        type="file" 
                        ref={fileInputRef} 
                        onChange={handleImageChange} 
                        accept="image/png, image/jpeg, image/jpg"
                        className="hidden" 
                    />
                </div>
                <div>
                    <div className="flex items-center gap-3">
                        <h1 className="text-2xl font-bold text-white">Your Profile</h1>
                        {isAdmin && (
                            <Link to="/admin" className="bg-cyan-500/20 text-cyan-400 text-xs font-bold px-2 py-1 rounded border border-cyan-500/30 hover:bg-cyan-500/30 transition-colors">
                                ADMIN DASHBOARD
                            </Link>
                        )}
                    </div>
                    <p className="text-gray-400 text-sm">{currentUser?.email}</p>
                    <button onClick={handleImageClick} className="text-cyan-400 text-xs hover:underline mt-1">
                        Change Profile Photo
                    </button>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-8">
                {message && (
                    <div className={`p-4 rounded-md ${message.type === 'success' ? 'bg-green-900/50 text-green-300 border border-green-800' : 'bg-red-900/50 text-red-300 border border-red-800'}`}>
                        {message.text}
                    </div>
                )}

                {/* PERSONAL INFO */}
                <div className="space-y-6">
                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider border-b border-gray-700 pb-2">Personal Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* First Name */}
                        <div>
                            <label htmlFor="firstName" className="block text-sm font-medium text-gray-400 mb-1">First Name</label>
                            <input
                                type="text"
                                name="firstName"
                                id="firstName"
                                value={formData.firstName || ''}
                                onChange={handleChange}
                                className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                            />
                        </div>
                        
                        {/* Last Name */}
                        <div>
                            <label htmlFor="lastName" className="block text-sm font-medium text-gray-400 mb-1">Last Name</label>
                            <input
                                type="text"
                                name="lastName"
                                id="lastName"
                                value={formData.lastName || ''}
                                onChange={handleChange}
                                className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        {/* Height */}
                        <div className="md:col-span-1">
                            <label className="block text-sm font-medium text-gray-400 mb-1">Height</label>
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    <input
                                        type="number"
                                        name="heightFeet"
                                        placeholder="Ft"
                                        value={formData.heightFeet || ''}
                                        onChange={handleChange}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                    />
                                    <span className="absolute right-3 top-2 text-gray-500 text-sm">ft</span>
                                </div>
                                <div className="relative flex-1">
                                    <input
                                        type="number"
                                        name="heightInches"
                                        placeholder="In"
                                        value={formData.heightInches || ''}
                                        onChange={handleChange}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                    />
                                    <span className="absolute right-3 top-2 text-gray-500 text-sm">in</span>
                                </div>
                            </div>
                        </div>

                        {/* Weight */}
                        <div>
                            <label htmlFor="weightLbs" className="block text-sm font-medium text-gray-400 mb-1">Weight</label>
                            <div className="relative">
                                <input
                                    type="number"
                                    name="weightLbs"
                                    id="weightLbs"
                                    value={formData.weightLbs || ''}
                                    onChange={handleChange}
                                    className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                />
                                <span className="absolute right-3 top-2 text-gray-500 text-sm">lbs</span>
                            </div>
                        </div>

                        {/* Bike Weight */}
                        <div>
                            <label htmlFor="bikeWeightLbs" className="block text-sm font-medium text-gray-400 mb-1">Bike Weight</label>
                            <div className="relative">
                                <input
                                    type="number"
                                    name="bikeWeightLbs"
                                    id="bikeWeightLbs"
                                    placeholder="19"
                                    value={formData.bikeWeightLbs || ''}
                                    onChange={handleChange}
                                    className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                />
                                <span className="absolute right-3 top-2 text-gray-500 text-sm">lbs</span>
                            </div>
                        </div>

                        {/* Age */}
                        <div>
                            <label htmlFor="age" className="block text-sm font-medium text-gray-400 mb-1">Age</label>
                            <input
                                type="number"
                                name="age"
                                id="age"
                                value={formData.age || ''}
                                onChange={handleChange}
                                className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Sex */}
                        <div>
                            <label htmlFor="sex" className="block text-sm font-medium text-gray-400 mb-1">Sex</label>
                            <select
                                name="sex"
                                id="sex"
                                value={formData.sex || ''}
                                onChange={handleChange}
                                className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                            >
                                <option value="" disabled>Select...</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Non-binary">Non-binary</option>
                                <option value="Prefer not to say">Prefer not to say</option>
                            </select>
                        </div>

                        {/* USAC License */}
                        <div>
                            <label htmlFor="usacLicenseNumber" className="block text-sm font-medium text-gray-400 mb-1">USA Cycling License #</label>
                            <input
                                type="text"
                                name="usacLicenseNumber"
                                id="usacLicenseNumber"
                                value={formData.usacLicenseNumber || ''}
                                onChange={handleChange}
                                className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                            />
                        </div>
                    </div>

                    {/* Team & FTP */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="teamName" className="block text-sm font-medium text-gray-400 mb-1">Team Name</label>
                            <input
                                type="text"
                                name="teamName"
                                id="teamName"
                                value={formData.teamName || ''}
                                onChange={handleChange}
                                className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                            />
                        </div>

                        <div>
                            <label htmlFor="ftp" className="block text-sm font-medium text-gray-400 mb-1">FTP (Watts)</label>
                            <input
                                type="number"
                                name="ftp"
                                id="ftp"
                                value={formData.ftp || ''}
                                onChange={handleChange}
                                className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                            />
                        </div>
                    </div>
                </div>

                {/* MATCHBOOK CALIBRATION */}
                <div>
                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider border-b border-gray-700 pb-2 mb-4">Matchbook Calibration</h3>
                    <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-700">
                        <p className="text-sm text-gray-400 mb-4">A 'match' represents a deep, anaerobic effort that dips significantly into your finite energy reserves. This is calculated dynamically based on your FTP and race history.</p>

                        {(((formData as UserProfile)?.racesAnalyzedForMatches || 0) < 5) && (
                            <div className="mb-6 bg-gray-800 p-4 rounded border border-gray-600">
                                <div className="flex justify-between text-sm text-gray-400 mb-2">
                                    <span>Calibration Progress</span>
                                    <span>{(formData as UserProfile)?.racesAnalyzedForMatches || 0} / 5 Races</span>
                                </div>
                                <div className="w-full bg-gray-700 rounded-full h-2">
                                    <div className="bg-cyan-500 h-2 rounded-full transition-all" style={{ width: `${(((formData as UserProfile)?.racesAnalyzedForMatches || 0) / 5) * 100}%` }}></div>
                                </div>
                                <p className="text-xs text-gray-500 mt-2">Upload more races to unlock a personalized matchbook. Defaulting to 4 matches.</p>
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Capacity</label>
                            <div className="flex items-center h-[42px] bg-gray-700 border border-gray-600 rounded-md px-4 text-xl overflow-hidden whitespace-nowrap">
                                {Array.from({ length: formData.estimatedMatches || ((((formData as UserProfile)?.racesAnalyzedForMatches || 0) < 5) ? 4 : 0) }).map((_, i) => (
                                    <span key={i} title="1 Match">🔥</span>
                                ))}
                                {(!formData.estimatedMatches && (((formData as UserProfile)?.racesAnalyzedForMatches || 0) >= 5)) && <span className="text-sm text-gray-500">Not set</span>}
                            </div>
                        </div>
                    </div>
                </div>

                {/* DEVICE INTEGRATIONS */}
                <div>
                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider border-b border-gray-700 pb-2 mb-4">Device Integrations</h3>
                    <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-700">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {/* Wahoo */}
                            <div className="bg-gray-800 p-4 rounded border border-gray-600 flex justify-between items-center">
                                <div>
                                    <h4 className="font-bold text-white">Wahoo</h4>
                                    {(formData as any).wahoo ? (
                                        <div className="mt-1">
                                            <p className="text-xs text-green-400 font-bold">Status: Connected</p>
                                            <p className="text-xs text-gray-400">
                                                Connected on: {(() => {
                                                    const d = (formData as any).wahoo.connectedAt;
                                                    if (!d) return 'Unknown';
                                                    return (typeof d.toDate === 'function' ? d.toDate() : new Date(d)).toLocaleDateString();
                                                })()}
                                            </p>
                                            <button 
                                                type="button"
                                                onClick={handleVerifyWahoo}
                                                disabled={isVerifyingWahoo}
                                                className="text-xs text-cyan-400 hover:text-cyan-300 hover:underline mt-1 flex items-center gap-1 disabled:opacity-50"
                                            >
                                                {isVerifyingWahoo ? 'Verifying...' : 'Test Connection'}
                                            </button>
                                        </div>
                                    ) : (
                                        <p className="text-xs text-gray-400">Sync ELEMNT rides</p>
                                    )}
                                </div>
                                {(formData as any).wahoo ? (
                                    <button
                                        type="button"
                                        onClick={handleDisconnectWahoo}
                                        className="px-3 py-1.5 bg-red-900/20 hover:bg-red-900/40 text-red-400 text-sm font-bold rounded border border-red-900/50 transition-colors"
                                    >
                                        Disconnect
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={handleConnectWahoo}
                                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold rounded border border-blue-500 transition-colors"
                                    >
                                        Connect
                                    </button>
                                )}
                            </div>

                            {/* Hammerhead */}
                            <div className="bg-gray-800 p-4 rounded border border-gray-600 flex justify-between items-center">
                                <div>
                                    <h4 className="font-bold text-white">Hammerhead</h4>
                                    {(formData as any).hammerhead ? (
                                        <div className="mt-1">
                                            <p className="text-xs text-green-400 font-bold">Status: Connected</p>
                                            <p className="text-xs text-gray-400">
                                                Connected on: {(() => {
                                                    const d = (formData as any).hammerhead.connectedAt;
                                                    if (!d) return 'Unknown';
                                                    return (typeof d.toDate === 'function' ? d.toDate() : new Date(d)).toLocaleDateString();
                                                })()}
                                            </p>
                                            <button 
                                                type="button"
                                                onClick={handleVerifyHammerhead}
                                                disabled={isVerifyingHammerhead}
                                                className="text-xs text-cyan-400 hover:text-cyan-300 hover:underline mt-1 flex items-center gap-1 disabled:opacity-50"
                                            >
                                                {isVerifyingHammerhead ? 'Verifying...' : 'Test Connection'}
                                            </button>
                                        </div>
                                    ) : (
                                        <p className="text-xs text-gray-400">Sync Karoo rides</p>
                                    )}
                                </div>
                                {(formData as any).hammerhead ? (
                                    <button type="button" onClick={handleDisconnectHammerhead} className="px-3 py-1.5 bg-red-900/20 hover:bg-red-900/40 text-red-400 text-sm font-bold rounded border border-red-900/50 transition-colors">Disconnect</button>
                                ) : (
                                    <button type="button" onClick={handleConnectHammerhead} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold rounded border border-blue-500 transition-colors">Connect</button>
                                )}
                            </div>

                            {/* Garmin */}
                            <div className="bg-gray-800 p-4 rounded border border-gray-600 flex justify-between items-center">
                                <div>
                                    <h4 className="font-bold text-white">Garmin</h4>
                                    <p className="text-xs text-gray-400">Sync Connect rides</p>
                                </div>
                                <button
                                    type="button"
                                disabled
                                className="px-3 py-1.5 bg-gray-700 text-gray-400 text-sm font-bold rounded border border-gray-600 cursor-not-allowed"
                                >
                                Coming Soon
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* PRIVACY ZONES */}
                <div>
                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider border-b border-gray-700 pb-2 mb-4">Privacy & Security</h3>
                     <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-700 mb-6 flex justify-between items-center">
                        <div>
                            <h4 className="font-bold text-white">Password</h4>
                            <p className="text-xs text-gray-400">Send a password reset link to your email.</p>
                            {resetPasswordStatus === 'success' && <p className="text-xs text-green-400 mt-1 font-bold">Email sent! Check your inbox.</p>}
                            {resetPasswordStatus === 'error' && <p className="text-xs text-red-400 mt-1 font-bold">Failed to send email.</p>}
                        </div>
                        <button
                            type="button"
                            onClick={handleResetPassword}
                            disabled={resetPasswordStatus === 'sending' || resetPasswordStatus === 'success'}
                            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm font-bold rounded border border-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {resetPasswordStatus === 'sending' ? 'Sending...' : resetPasswordStatus === 'success' ? 'Sent' : 'Reset Password'}
                        </button>
                    </div>
                    <PrivacyZoneManager 
                        userProfile={formData as UserProfile} 
                        onChange={handlePrivacyZonesChange} 
                    />
                </div>

                <div className="pt-4 flex justify-end border-t border-gray-700">
                    <button
                        type="submit"
                        disabled={isSaving}
                        className="flex items-center gap-2 px-6 py-2 bg-cyan-600 text-white font-bold rounded-lg hover:bg-cyan-500 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSaving ? (
                            <>Processing...</>
                        ) : (
                            <>
                                <PencilIcon className="w-4 h-4" />
                                Save Profile
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>

        {/* RACE CALENDAR SECTION */}
        <div className="bg-gray-800 rounded-lg shadow-xl border border-gray-700 overflow-hidden">
             <div className="p-6 border-b border-gray-700 flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-white">My Event Calendar</h2>
                    <p className="text-gray-400 text-sm">Upcoming events you plan to attend</p>
                </div>
                <button 
                    onClick={() => setIsSearchOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-cyan-400 font-bold rounded-lg hover:bg-gray-600 transition-colors border border-gray-600"
                >
                    <PlusIcon className="w-4 h-4" />
                    Find Event
                </button>
            </div>
            
            <div className="p-6">
                {plannedEvents.length > 0 ? (
                    <div className="space-y-3">
                        {plannedEvents.map(event => (
                            <div key={event.id} className="flex items-center justify-between bg-gray-700/50 p-4 rounded-lg border border-gray-600">
                                <div>
                                    <h3 className="font-bold text-white">{event.name}</h3>
                                    <div className="text-gray-400 text-sm mt-1 space-x-3">
                                        <span>{new Date(event.date).toLocaleDateString(undefined, { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric'})}</span>
                                        <span>|</span>
                                        <span>{event.location}</span>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => handleRemoveEvent(event)}
                                    className="p-2 text-gray-500 hover:text-red-400 transition-colors rounded-full hover:bg-gray-600"
                                    title="Remove from calendar"
                                >
                                    <TrashIcon className="w-5 h-5" />
                                </button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8 text-gray-500 bg-gray-900/50 rounded-lg border border-gray-700 border-dashed">
                        No events planned yet. Use "Find Event" to build your calendar.
                    </div>
                )}
            </div>
        </div>

        {/* SAVED GROUP RIDES SECTION */}
        <div className="bg-gray-800 rounded-lg shadow-xl border border-gray-700 overflow-hidden">
            <div className="p-6 border-b border-gray-700">
                <h2 className="text-xl font-bold text-white">Saved Group Rides</h2>
                <p className="text-gray-400 text-sm">Group rides you've favorited</p>
            </div>
            <div className="p-6">
                {favoriteGroupRides.length > 0 ? (
                    <div className="space-y-3">
                        {favoriteGroupRides
                            .slice()
                            .sort((a, b) => b.savedAt - a.savedAt)
                            .map(ride => (
                                <div key={ride.id} className="flex items-center justify-between bg-gray-700/50 p-4 rounded-lg border border-gray-600">
                                    <div>
                                        <span className="text-xs font-bold text-amber-400">⭐ Group Ride</span>
                                        <h3 className="font-bold text-white">{ride.name}</h3>
                                        {ride.locationName && (
                                            <p className="text-gray-400 text-sm mt-0.5">{ride.locationName}</p>
                                        )}
                                        <p className="text-gray-500 text-xs mt-1">
                                            Saved {new Date(ride.savedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Link
                                            to={`/ride/${ride.id}`}
                                            state={{ from: 'profile' }}
                                            className="px-3 py-1.5 bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-300 text-xs font-bold rounded border border-cyan-500/40 transition-colors"
                                        >
                                            View
                                        </Link>
                                        <button
                                            onClick={() => handleRemoveFavoriteGroupRide(ride.id)}
                                            className="p-2 text-gray-500 hover:text-red-400 transition-colors rounded-full hover:bg-gray-600"
                                            title="Remove from saved rides"
                                        >
                                            <TrashIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                ) : (
                    <div className="text-center py-8 text-gray-500 bg-gray-900/50 rounded-lg border border-gray-700 border-dashed">
                        No saved group rides yet. Click "Favorite Ride" on any group ride page.
                    </div>
                )}
            </div>
        </div>
      </div>
      
      {isSearchOpen && (
          <EventSearchModal 
            onClose={() => setIsSearchOpen(false)} 
            onAddEvent={handleAddEvent}
            existingEventIds={new Set(plannedEvents.map(e => e.id))}
          />
      )}
    </div>
  );
};

export default UserProfilePage;
